import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { InputWork, InputWorkTitle } from '../../shared/component/headless/input/InputBody';
import { DimensionsType, DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, TextAlignment, usesWorkDuration } from '../../shared/enum/EnumRepository';
import { convertWorkSizeToString, PEWorkData, SizeData } from '../../shared/dto/EntityRepository';
import { useValidation } from '../../shared/hooks/useValidation';
import { Info, VariableSizeLabel } from './WorkInfo';
import SizeInputForPE from './SizeInputForPE';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextAreaDiv from '../../shared/component/molecule/MoleculeTextAreaDiv';
import { InputWorkTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import { useEditMode } from '../../shared/hooks/useEditMode';
import MobileBanWrapper from '../../shared/component/MobileBanWrapper';
import { TitleOblique } from './WorkInfo';
import { useSettingStore } from '../../shared/store/settingStore';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';

export interface WorkInfoProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  imageStyle: ImageStyle;
  infoType: PEInfoType;
  data: PEWorkData;
}

const WorkInfoRightComp: React.FC<WorkInfoProps> = ({ peId, alignment, displaySize, imageStyle, infoType, data }) => {
  const handleChange = useProjectElementListStore((s) => s.updateWork);
  const { checkType } = useValidation();
  const { isEditMode } = useEditMode();
  const setting = useSettingStore((state) => state.setting);
  const artistName = !isEditMode && shouldShowWorkArtist(setting)
    ? resolveWorkArtistName(data.artist)
    : '';

  const handleChangeWithValidate = (field: keyof PEWorkData, value: string | SizeData | DimensionsType) => {
    if (!checkType(field, value)) {
      return;
    };
    handleChange(peId, { [field]: value });
  }

  const defaultAlignment = alignment || DisplayAlignment.CENTER;
  const defaultDisplaySize = displaySize || DisplaySize.BIG;
  const defaultImageStyle = imageStyle || ImageStyle.LANDSCAPE;

  const inlineInfo = useMemo(() => {
    const sizeOrDuration =
      usesWorkDuration(data.workType)
        ? (data.duration || "—")
        : convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText);
    return data.prodYear + ", " + data.material + ", " + sizeOrDuration;
  }, [data.prodYear, data.material, data.size, data.sizeText, data.dimensionsType, data.workType, data.duration]);

  //useMemo transition 영향 없음
  const rightAligned = useMemo(
    () => isRightAligned(defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType]
  );

  const rightAlignedOnly = useMemo(
    () => isRightAlignedOnly(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  const flexGrow = useMemo(
    () => getFlexGrow(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  const renderInlineInfo = () => {
    if (isEditMode) {
      return (
        <>
          <MoleculeInputDiv
            value={data.prodYear}
            placeholder={"Year"}
            handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
            inputStyle={InfoWorkInputRightOnly}
            StyledDiv={Info}
          />
          <MoleculeTextAreaDiv
            value={data.material}
            placeholder={"Material"}
            handleChange={(e) => handleChangeWithValidate("material", e.target.value)}
            textareaStyle={InputWorkTextArea}
            StyledDiv={Info}
            alignment={TextAlignment.CENTER}
          />
          {usesWorkDuration(data.workType) ? (
            <MoleculeInputDiv
              value={data.duration}
              placeholder={"00:00:00 (hh:mm:ss)"}
              handleChange={(e) => handleChangeWithValidate("duration", e.target.value)}
              inputStyle={InfoWorkInputRightOnly}
              StyledDiv={Info}
            />
          ) : (
            <>
              {data.dimensionsType === DimensionsType.FIXED && (
                <SizeInputForPE
                  value={data.size}
                  onChange={(next) => handleChangeWithValidate("size", next)}
                  StyledDiv={Info}
                />
              )}
              {data.dimensionsType === DimensionsType.CUSTOM && (
                <MoleculeInputDiv
                  value={data.sizeText ?? ''}
                  placeholder={"Custom size"}
                  handleChange={(e) => handleChangeWithValidate("sizeText", e.target.value)}
                  inputStyle={InfoWorkInputRightOnly}
                  StyledDiv={Info}
                />
              )}
              {data.dimensionsType === DimensionsType.VARIABLE && (
                <VariableSizeLabel>Dimensions variable</VariableSizeLabel>
              )}
            </>
          )}
        </>
      )
    } else {
      return (
        <>
          {inlineInfo}
        </>
      )
    }
  }

  return (
    <MobileBanWrapper>
      <ImgContentInfoWrapperRight $flexGrow={flexGrow} $rightAligned={rightAligned} $rightAlignedOnly={rightAlignedOnly}>
        {infoType !== PEInfoType.NONE && (
          <InfoWrapperRight
            $rightAlignedOnly={rightAlignedOnly}
            $displaySize={defaultDisplaySize}
            $imageStyle={defaultImageStyle}
          >
            <InlineInfo $rightAlignedOnly={rightAlignedOnly}>
              {artistName && <span>{artistName}, </span>}<TitleOblique>{data.title}, </TitleOblique>{inlineInfo}
            </InlineInfo>
            {isEditMode &&
              <>
                <MoleculeInputDiv
                  value={data.title}
                  placeholder={""}
                  handleChange={(e) => handleChangeWithValidate("title", e.target.value)}
                  inputStyle={InputWorkTitle}
                  StyledDiv={TitleRight}
                />
                <WorkInfo $isEditMode={isEditMode}>
                  {renderInlineInfo()}
                </WorkInfo>
              </>
            }
          </InfoWrapperRight>
        )}
      </ImgContentInfoWrapperRight>
    </MobileBanWrapper>
  );
};

export const getFlexGrow = (
  $displayAlignment: DisplayAlignment,
  $displaySize?: DisplaySize,
  $imageStyle?: ImageStyle
): string => {
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
    return '0';
  }
  switch ($displayAlignment) {
    case DisplayAlignment.LEFT_FLUSH:     // ┏img──────┛
      return '1';
    case DisplayAlignment.LEFT_OFFSET:    // ┏──¼│img───┛
      return '6';
    case DisplayAlignment.RIGHT_OFFSET:   // ┏───img│¼──┛
      return '2';
    case DisplayAlignment.RIGHT_FLUSH:    // ┏──────img┛
      return '0';
    case DisplayAlignment.CENTER:         // ┏──½│img│½──┛
    default:
      return '1';
  }
};
/**
 * 이미지가 오른쪽 정렬 되어있으면 (RIGHT_FLUSH, RIGHT_OFFSET)  
 * 이미지가 중앙 정렬 되어있으면 (CENTER)  
 * 보이지 않는다.
 */
export const isRightAligned = (
  $displayAlignment: DisplayAlignment,
  $displaySize?: DisplaySize,
  $imageStyle?: ImageStyle,
  $infoType: PEInfoType = PEInfoType.INLINE,
): boolean => {
  if ($infoType === PEInfoType.NONE) return true;
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
    return true;
  }
  return [
    DisplayAlignment.RIGHT_FLUSH,
    DisplayAlignment.RIGHT_OFFSET,
    DisplayAlignment.CENTER
  ].includes($displayAlignment);
};

/**
 * 이미지가 오른쪽 정렬 되어있으면 (RIGHT_FLUSH, RIGHT_OFFSET)  
 * 이미지가 중앙 정렬 되어있으면 (CENTER)  
 * WIDE + LANDSCAPE이면 alignment와 무관하게 CENTER와 같다.  
 * 보이지 않는다.
 */
export const isRightAlignedOnly = (
  $displayAlignment: DisplayAlignment,
  $displaySize?: DisplaySize,
  $imageStyle?: ImageStyle,
): boolean => {
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
    return true;
  }
  return [
    DisplayAlignment.RIGHT_FLUSH,
    DisplayAlignment.RIGHT_OFFSET,
    DisplayAlignment.CENTER
  ].includes($displayAlignment);
};

export const ImgContentInfoWrapperRight = styled.div<{ $flexGrow: string, $rightAligned: boolean, $rightAlignedOnly: boolean }>`
  display: flex;
  flex: ${({ $flexGrow }) => `${$flexGrow} 1 0%`};
  min-width: 0;
  justify-content: flex-start;
  opacity: ${({ $rightAligned }) => $rightAligned ? 0 : 1};
  transform: ${({ $rightAlignedOnly }) => $rightAlignedOnly ? 'translateX(-15vw)' : 'translateX(0vw)'};
  z-index: ${({ $rightAligned }) => $rightAligned ? -10 : 1};
  pointer-events: ${({ $rightAligned }) => $rightAligned ? 'none' : 'auto'};
  transition: flex-grow 0.7s ease, opacity 0.5s ease, transform 0.6s ease;
`;

export const InfoWrapperRight = styled.div <{
  $rightAlignedOnly: boolean,
  $displaySize: DisplaySize,
  $imageStyle: ImageStyle
}>`
  width: ${({ $rightAlignedOnly, $displaySize, $imageStyle }) => {
    if ($rightAlignedOnly) {
      return '0vw';
    }
    if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
      return '0vw';
    }
    return '98%';
  }};
  max-width: 19.5vw;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  // overflow: hidden;
`;

export const TitleRight = styled.h2`
  width: 100%;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: left;
  font-style: oblique;
  ${({ theme }) => theme.typography.Body_02_2};
`;


const WorkInfo = styled.div<{ $isEditMode: boolean }>`
  width: 100%;
  // max-width: 19.5vw;
  display: flex;
  flex-direction: ${({ $isEditMode }) => $isEditMode ? 'column' : 'row'};
  justify-content: flex-start;
  align-items: center;
  gap: 1px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: left;
`;

const InfoWorkInputRightOnly = styled(InputWork)`
  width: 100%;
`;

const InlineInfo = styled.div<{ $rightAlignedOnly: boolean }>`
  display: ${({ $rightAlignedOnly }) => ($rightAlignedOnly ? 'none' : 'block')};
  width: 100%;
  min-width: 0;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;


export default WorkInfoRightComp;
